/** @module @liquidefy/code-editor/layout/layout-wordwrap @version 1.0.0 @license LQD Research LLC */

// ═══ §1 IMPORTS ═══

import type {
  Position,
  WordWrapMode,
  EditorResult,
} from '../code-editor-types.js';

import {
  ok,
  err,
  clamp,
} from '../code-editor.js';

/**
 * A single wrapped segment of a logical line.
 */
export interface WrappedSegment {
  readonly content: string;
  readonly startColumn: number;
  readonly endColumn: number;
}

/**
 * Per-line wrap info: where the breaks occur and how many wrapped lines result.
 */
interface WrapInfo {
  readonly breakPoints: number[];
  readonly wrappedLineCount: number;
}

// ═══ §2 WORD WRAPPING — wrap single lines, batch, count wrapped lines ═══

/**
 * Wraps a single line of text at the given column according to the wrap mode.
 * Returns an array of wrapped segments with their break indices.
 */
export function wrapLine(
  line: string,
  column: number,
  mode: WordWrapMode,
  tabSize: number,
): EditorResult<WrappedSegment[]> {
  if (mode === 'off') {
    return ok([{ content: line, startColumn: 0, endColumn: line.length }], 'WRAP_OFF', 'No wrapping');
  }

  if (column <= 0) return err('INVALID_COLUMN', 'Wrap column must be positive');

  const expanded = expandTabs(line, tabSize);
  const breakPoints = findBreakPoints(expanded, column, mode);

  if (breakPoints.length === 0) {
    return ok([{ content: line, startColumn: 0, endColumn: line.length }], 'NO_WRAP', 'Line fits');
  }

  const segments: WrappedSegment[] = [];
  let prevIdx = 0;

  for (const bp of breakPoints) {
    const mappedBreak = mapExpandedToOriginal(line, bp, tabSize);
    segments.push({
      content: line.substring(prevIdx, mappedBreak),
      startColumn: prevIdx,
      endColumn: mappedBreak,
    });
    prevIdx = mappedBreak;
  }

  // Final segment
  segments.push({
    content: line.substring(prevIdx),
    startColumn: prevIdx,
    endColumn: line.length,
  });

  return ok(segments, 'WRAPPED', `Line wrapped into ${segments.length} segments`);
}

/**
 * Wraps all lines in a document.
 */
export function wrapLines(
  lines: string[],
  column: number,
  mode: WordWrapMode,
  tabSize: number,
): EditorResult<WrappedSegment[][]> {
  const results: WrappedSegment[][] = [];
  for (const line of lines) {
    const result = wrapLine(line, column, mode, tabSize);
    if (!result.success) return err(result.code, result.message);
    results.push(result.value);
  }
  return ok(results, 'LINES_WRAPPED', `Wrapped ${lines.length} lines`);
}

/**
 * Returns the wrapped line count for a single logical line.
 */
export function computeWrappedLineCount(
  line: string,
  column: number,
  mode: WordWrapMode,
  tabSize: number,
): number {
  if (mode === 'off' || column <= 0) return 1;
  const expanded = expandTabs(line, tabSize);
  const breakPoints = findBreakPoints(expanded, column, mode);
  return breakPoints.length + 1;
}

/**
 * Returns the total number of wrapped lines across all logical lines.
 */
export function totalWrappedLines(
  lines: string[],
  column: number,
  mode: WordWrapMode,
  tabSize: number,
): number {
  let total = 0;
  for (const line of lines) {
    total += computeWrappedLineCount(line, column, mode, tabSize);
  }
  return total;
}

// ═══ §3 BREAK POINTS — find where wraps occur, character classification ═══

/** Characters after which a line break is preferred. */
export const BREAK_AFTER_CHARS = ' \t-/\\|,;:!?)}]>'.split('');

/** Characters before which a line break is preferred. */
export const BREAK_BEFORE_CHARS = '({[<'.split('');

/** Characters that should not be broken. */
export const NO_BREAK_CHARS = '\u00A0\u2007\u202F'.split('');

/**
 * Finds the column indices where line breaks should occur for wrapping.
 */
export function findBreakPoints(
  line: string,
  column: number,
  mode: WordWrapMode,
): number[] {
  if (line.length <= column) return [];

  const breaks: number[] = [];
  let pos = 0;

  while (pos + column < line.length) {
    let breakAt = -1;

    if (mode === 'wordWrap' || mode === 'bounded' || mode === 'on') {
      // Search backwards from the column boundary for a break opportunity
      for (let i = pos + column; i > pos; i--) {
        const char = line[i - 1] ?? '';
        const nextChar = i < line.length ? (line[i] ?? '') : '';
        if (isBreakOpportunity(char, nextChar)) {
          breakAt = i;
          break;
        }
      }
    }

    // If no word break found, break at the column boundary (hard wrap)
    if (breakAt === -1) {
      breakAt = pos + column;
    }

    // Avoid infinite loops
    if (breakAt <= pos) {
      breakAt = pos + column;
    }

    breaks.push(breakAt);
    pos = breakAt;
  }

  return breaks;
}

/**
 * Determines if a break opportunity exists between char and nextChar.
 * Simplified Unicode line break algorithm.
 */
export function isBreakOpportunity(char: string, nextChar: string): boolean {
  // No break around non-breaking characters
  if (NO_BREAK_CHARS.includes(char) || NO_BREAK_CHARS.includes(nextChar)) {
    return false;
  }

  // Break after certain punctuation/whitespace
  if (BREAK_AFTER_CHARS.includes(char)) return true;

  // Break before opening brackets
  if (BREAK_BEFORE_CHARS.includes(nextChar)) return true;

  // Break at CJK character boundaries
  if (isCJK(char) || isCJK(nextChar)) return true;

  return false;
}

// ═══ §4 WRAPPED LINE MAPPING — convert between wrapped and logical coordinates ═══

/**
 * Given a wrapped line number (0-based across all wrapped lines), returns the logical line index.
 */
export function wrappedToLogicalLine(wrappedLine: number, wrapInfos: WrapInfo[]): number {
  let accumulated = 0;
  for (let i = 0; i < wrapInfos.length; i++) {
    const count = wrapInfos[i]!.wrappedLineCount;
    if (wrappedLine < accumulated + count) {
      return i;
    }
    accumulated += count;
  }
  return Math.max(0, wrapInfos.length - 1);
}

/**
 * Given a logical line number, returns the range [startWrappedLine, endWrappedLine) in wrapped space.
 */
export function logicalToWrappedLines(
  logicalLine: number,
  wrapInfos: WrapInfo[],
): { start: number; end: number } {
  let accumulated = 0;
  for (let i = 0; i < wrapInfos.length; i++) {
    const count = wrapInfos[i]!.wrappedLineCount;
    if (i === logicalLine) {
      return { start: accumulated, end: accumulated + count };
    }
    accumulated += count;
  }
  return { start: accumulated, end: accumulated + 1 };
}

/**
 * Converts a wrapped-space position (wrappedLine, column) to a logical (line, column).
 */
export function wrappedPositionToLogical(
  wrappedLine: number,
  column: number,
  wrapInfos: WrapInfo[],
): Position {
  let accumulated = 0;
  for (let i = 0; i < wrapInfos.length; i++) {
    const info = wrapInfos[i]!;
    if (wrappedLine < accumulated + info.wrappedLineCount) {
      const segmentIndex = wrappedLine - accumulated;
      const startCol = segmentIndex === 0 ? 0 : (info.breakPoints[segmentIndex - 1] ?? 0);
      return { line: i, column: startCol + column };
    }
    accumulated += info.wrappedLineCount;
  }
  return { line: Math.max(0, wrapInfos.length - 1), column };
}

/**
 * Converts a logical position to a wrapped-space position.
 */
export function logicalToWrappedPosition(
  position: Position,
  wrapInfos: WrapInfo[],
): { wrappedLine: number; column: number } {
  if (position.line >= wrapInfos.length) {
    let total = 0;
    for (const info of wrapInfos) total += info.wrappedLineCount;
    return { wrappedLine: total, column: position.column };
  }

  let wrappedLineBase = 0;
  for (let i = 0; i < position.line; i++) {
    wrappedLineBase += wrapInfos[i]!.wrappedLineCount;
  }

  const info = wrapInfos[position.line]!;
  let segmentIndex = 0;
  let segmentStartCol = 0;

  for (let bp = 0; bp < info.breakPoints.length; bp++) {
    if (position.column >= (info.breakPoints[bp] ?? 0)) {
      segmentIndex = bp + 1;
      segmentStartCol = info.breakPoints[bp] ?? 0;
    } else {
      break;
    }
  }

  return {
    wrappedLine: wrappedLineBase + segmentIndex,
    column: position.column - segmentStartCol,
  };
}

/**
 * Builds WrapInfo for a single line.
 */
export function buildWrapInfo(
  line: string,
  column: number,
  mode: WordWrapMode,
  tabSize: number,
): WrapInfo {
  if (mode === 'off' || column <= 0) {
    return { breakPoints: [], wrappedLineCount: 1 };
  }
  const expanded = expandTabs(line, tabSize);
  const breakPoints = findBreakPoints(expanded, column, mode);
  // Map break points back to original coordinates
  const mappedBreaks = breakPoints.map((bp) => mapExpandedToOriginal(line, bp, tabSize));
  return { breakPoints: mappedBreaks, wrappedLineCount: mappedBreaks.length + 1 };
}

// ═══ §5 INDENTATION IN WRAPS — wrapped line indentation and content extraction ═══

/**
 * Computes the indentation string for a wrapped continuation line.
 */
export function computeWrappedIndent(
  line: string,
  tabSize: number,
  wrappedIndent: 'none' | 'same' | 'indent' | 'deepIndent',
): string {
  if (wrappedIndent === 'none') return '';

  // Measure the leading whitespace of the original line
  let leadingSpaces = 0;
  for (let i = 0; i < line.length; i++) {
    if (line[i] === ' ') {
      leadingSpaces++;
    } else if (line[i] === '\t') {
      leadingSpaces += tabSize;
    } else {
      break;
    }
  }

  switch (wrappedIndent) {
    case 'same':
      return ' '.repeat(leadingSpaces);
    case 'indent':
      return ' '.repeat(leadingSpaces + tabSize);
    case 'deepIndent':
      return ' '.repeat(leadingSpaces + tabSize * 2);
    default:
      return '';
  }
}

/**
 * Returns the content of the nth wrapped segment of a line, with optional indentation.
 */
export function wrappedLineContent(
  line: string,
  segmentIndex: number,
  wrapInfo: WrapInfo,
  wrappedIndent: 'none' | 'same' | 'indent' | 'deepIndent',
): string {
  const breakPoints = wrapInfo.breakPoints;
  const totalSegments = wrapInfo.wrappedLineCount;

  if (segmentIndex < 0 || segmentIndex >= totalSegments) return '';

  let startCol: number;
  let endCol: number;

  if (segmentIndex === 0) {
    startCol = 0;
    endCol = breakPoints.length > 0 ? (breakPoints[0] ?? line.length) : line.length;
  } else if (segmentIndex === totalSegments - 1) {
    startCol = breakPoints[segmentIndex - 1] ?? 0;
    endCol = line.length;
  } else {
    startCol = breakPoints[segmentIndex - 1] ?? 0;
    endCol = breakPoints[segmentIndex] ?? line.length;
  }

  const raw = line.substring(startCol, endCol);

  if (segmentIndex === 0) {
    return raw;
  }

  // For continuation lines, add wrapped indentation
  const indent = computeWrappedIndent(line, 4, wrappedIndent);
  return indent + raw;
}

// ═══ INTERNAL HELPERS ═══

/**
 * Expands tabs to spaces for column calculation purposes.
 */
function expandTabs(line: string, tabSize: number): string {
  let result = '';
  let col = 0;
  for (const ch of line) {
    if (ch === '\t') {
      const spaces = tabSize - (col % tabSize);
      result += ' '.repeat(spaces);
      col += spaces;
    } else {
      result += ch;
      col++;
    }
  }
  return result;
}

/**
 * Maps an index in the tab-expanded string back to the original string index.
 */
function mapExpandedToOriginal(original: string, expandedIndex: number, tabSize: number): number {
  let expandedPos = 0;
  for (let i = 0; i < original.length; i++) {
    if (expandedPos >= expandedIndex) return i;
    if (original[i] === '\t') {
      expandedPos += tabSize - (expandedPos % tabSize);
    } else {
      expandedPos++;
    }
  }
  return original.length;
}

/**
 * Simplified CJK character detection (CJK Unified Ideographs range).
 */
function isCJK(char: string): boolean {
  const code = char.charCodeAt(0);
  return (
    (code >= 0x4E00 && code <= 0x9FFF) ||   // CJK Unified
    (code >= 0x3400 && code <= 0x4DBF) ||   // CJK Extension A
    (code >= 0xF900 && code <= 0xFAFF) ||   // CJK Compat
    (code >= 0x3000 && code <= 0x303F) ||   // CJK Symbols
    (code >= 0x3040 && code <= 0x309F) ||   // Hiragana
    (code >= 0x30A0 && code <= 0x30FF) ||   // Katakana
    (code >= 0xAC00 && code <= 0xD7AF)      // Hangul
  );
}
